import React, { useState } from 'react';
import { Search, Hash, TrendingUp, Users, Star, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useAthleteSearch } from '../hooks/useAthleteSearch';
import { useTrendingHashtags, useFeaturedAthletes } from '../hooks/useDiscover';
import { useDiscoverAthletes } from '../hooks/useDiscoverAthletes';
import { useAuth } from '@/contexts/AuthContext';
import { useFollowActions } from '../hooks/useFollowActions';

export function SocialDiscoverPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  
  const { data: searchResults, isLoading: isSearching } = useAthleteSearch(searchQuery);
  const { data: trendingHashtags, isLoading: isLoadingHashtags } = useTrendingHashtags();
  const { data: featuredAthletes, isLoading: isLoadingFeatured } = useFeaturedAthletes();
  const { popularAthletes, isLoadingPopular } = useDiscoverAthletes(user?.id);
  const { followUser, unfollowUser, isFollowing } = useFollowActions();

  const isSearchActive = searchQuery.length >= 2;

  return (
    <div className="space-y-6 pb-20">
      {/* Search Bar */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search athletes by name or username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Search Results */}
      {isSearchActive && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Search className="h-4 w-4" />
              Search Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isSearching ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : searchResults && searchResults.length > 0 ? (
              <div className="space-y-3">
                {searchResults.map((athlete) => (
                  <AthleteRow
                    key={athlete.id}
                    athlete={athlete}
                    isFollowing={athlete.isFollowing}
                    onFollow={() => followUser(athlete.id)}
                    onUnfollow={() => unfollowUser(athlete.id)}
                    isLoggedIn={!!user}
                  />
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">
                No athletes found for "{searchQuery}"
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Non-search content */}
      {!isSearchActive && (
        <>
          {/* Trending Hashtags */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Trending Hashtags
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingHashtags ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : trendingHashtags && trendingHashtags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {trendingHashtags.map((hashtag) => (
                    <Link
                      key={hashtag.id}
                      to={`/athlete-dashboard/wall/tag/${hashtag.tag}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent hover:bg-accent/80 transition-colors"
                    >
                      <Hash className="h-3 w-3 text-primary" />
                      <span className="text-sm font-medium">{hashtag.tag}</span>
                      <span className="text-xs text-muted-foreground ml-1">
                        {hashtag.post_count}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No trending hashtags yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Featured Athletes */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Star className="h-4 w-4 text-yellow-500" />
                Featured Athletes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingFeatured ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : featuredAthletes && featuredAthletes.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {featuredAthletes.map((featured) => (
                    <FeaturedAthleteCard
                      key={featured.id}
                      athlete={featured.profile}
                      onFollow={() => followUser(featured.profile_id)}
                      isLoggedIn={!!user}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No featured athletes yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Popular Athletes */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Popular Athletes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingPopular ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : popularAthletes && popularAthletes.length > 0 ? (
                <div className="space-y-3">
                  {popularAthletes.slice(0, 10).map((athlete) => (
                    <AthleteRow
                      key={athlete.id}
                      athlete={{
                        id: athlete.id,
                        first_name: athlete.firstName,
                        last_name: athlete.lastName,
                        username: athlete.username,
                        avatar_url: athlete.avatarUrl,
                        primary_sport_id: null,
                        isFollowing: athlete.isFollowed,
                      }}
                      isFollowing={athlete.isFollowed}
                      onFollow={() => followUser(athlete.id)}
                      onUnfollow={() => unfollowUser(athlete.id)}
                      isLoggedIn={!!user}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No athletes to discover yet
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

interface AthleteRowProps {
  athlete: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
    avatar_url: string | null;
    primary_sport_id: string | null;
    isFollowing: boolean;
  };
  isFollowing: boolean;
  onFollow: () => void;
  onUnfollow: () => void;
  isLoggedIn: boolean;
}

function AthleteRow({ athlete, isFollowing, onFollow, onUnfollow, isLoggedIn }: AthleteRowProps) {
  const displayName = [athlete.first_name, athlete.last_name].filter(Boolean).join(' ') || 'Athlete';
  const initials = `${athlete.first_name?.[0] || ''}${athlete.last_name?.[0] || ''}`.toUpperCase() || 'A';

  return (
    <div className="flex items-center justify-between py-2">
      <Link to={`/profile/${athlete.id}`} className="flex items-center gap-3 flex-1 min-w-0">
        <Avatar className="h-10 w-10">
          <AvatarImage src={athlete.avatar_url || undefined} />
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="font-medium truncate">{displayName}</p>
          {athlete.username && (
            <p className="text-sm text-muted-foreground truncate">@{athlete.username}</p>
          )}
        </div>
      </Link>
      {isLoggedIn && (
        <Button
          variant={isFollowing ? "outline" : "default"}
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            isFollowing ? onUnfollow() : onFollow();
          }}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </Button>
      )}
    </div>
  );
}

interface FeaturedAthleteCardProps {
  athlete: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
    avatar_url: string | null;
  };
  onFollow: () => void;
  isLoggedIn: boolean;
}

function FeaturedAthleteCard({ athlete, onFollow, isLoggedIn }: FeaturedAthleteCardProps) {
  const displayName = [athlete.first_name, athlete.last_name].filter(Boolean).join(' ') || 'Athlete';
  const initials = `${athlete.first_name?.[0] || ''}${athlete.last_name?.[0] || ''}`.toUpperCase() || 'A';

  return (
    <Link
      to={`/profile/${athlete.id}`}
      className="flex flex-col items-center p-3 rounded-lg bg-accent/50 hover:bg-accent transition-colors text-center"
    >
      <Avatar className="h-14 w-14 mb-2">
        <AvatarImage src={athlete.avatar_url || undefined} />
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <p className="font-medium text-sm truncate w-full">{displayName}</p>
      {athlete.username && (
        <p className="text-xs text-muted-foreground truncate w-full">@{athlete.username}</p>
      )}
    </Link>
  );
}
